import { useEffect, useMemo, useState } from "react";
import { formatDateInZone } from "@nurion/datetime/format-date-in-zone";

import {
  getUserAccountDataLike,
  getUserPresence,
  getUserProfile,
  subscribePresence,
  type Client,
  type PresenceSnapshot,
  type UserStatusAccountData,
  type UserTimezoneAccountData,
} from "~/lib/matrix";
import { displayNameForUser } from "~/lib/display-name";

export const USER_STATUS_EVENT_TYPE = "com.nurion.status" as const;
export const USER_TIMEZONE_EVENT_TYPE = "com.nurion.timezone" as const;

export interface UserProfileState {
  displayName: string;
  avatarUrl: string | null;
  matrixId: string;
  presence: PresenceSnapshot;
  status: string | null;
  statusMsg: string | null;
  timezone: string | null;
  localTime: string | null;
  meetingUrl: string | null;
}

const DEFAULT_PRESENCE: PresenceSnapshot = { state: "offline", lastActive: null };

export function validTimezone(value: unknown): string | null {
  if (!value || typeof value !== "object") return null;
  const timezone = (value as Partial<UserTimezoneAccountData>).timezone;
  if (typeof timezone !== "string" || timezone.length === 0) return null;
  try {
    formatDateInZone(new Date().toISOString(), timezone);
    return timezone;
  } catch {
    return null;
  }
}

export function safeMeetingUrl(value: unknown): string | null {
  if (typeof value !== "string") return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" && url.protocol !== "http:") return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function normaliseStatus(value: unknown): {
  status: string | null;
  statusMsg: string | null;
  meetingUrl: string | null;
} {
  if (!value || typeof value !== "object") {
    return { status: null, statusMsg: null, meetingUrl: null };
  }
  const raw = value as Partial<UserStatusAccountData>;
  if (typeof raw.expiresAt === "number" && raw.expiresAt <= Date.now()) {
    return { status: null, statusMsg: null, meetingUrl: null };
  }
  const message = typeof raw.message === "string" ? raw.message : null;
  const statusMsg = typeof raw.statusMsg === "string" ? raw.statusMsg : message;
  const emoji = typeof raw.emoji === "string" ? raw.emoji : null;
  const meetingUrl = safeMeetingUrl(raw.meetingUrl);
  return { status: emoji, statusMsg, meetingUrl };
}

function formatLocalTime(timezone: string | null, tick: number): string | null {
  if (!timezone) return null;
  try {
    return formatDateInZone(new Date(tick).toISOString(), timezone, {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
}

export function useUserProfile(
  client: Client | null,
  userId: string,
  fallback?: { displayName?: string | null; avatarUrl?: string | null },
): UserProfileState {
  const [displayName, setDisplayName] = useState(displayNameForUser(fallback?.displayName, userId));
  const [avatarUrl, setAvatarUrl] = useState<string | null>(fallback?.avatarUrl ?? null);
  const [presence, setPresence] = useState<PresenceSnapshot>(DEFAULT_PRESENCE);
  const [statusData, setStatusData] = useState<ReturnType<typeof normaliseStatus>>({
    status: null,
    statusMsg: null,
    meetingUrl: null,
  });
  const [timezone, setTimezone] = useState<string | null>(null);
  const [tick, setTick] = useState(Date.now());

  useEffect(() => {
    setDisplayName(displayNameForUser(fallback?.displayName, userId));
    setAvatarUrl(fallback?.avatarUrl ?? null);
  }, [fallback?.avatarUrl, fallback?.displayName, userId]);

  useEffect(() => {
    if (!client) {
      setPresence(DEFAULT_PRESENCE);
      setStatusData({ status: null, statusMsg: null, meetingUrl: null });
      setTimezone(null);
      return;
    }
    let cancelled = false;
    const unsub = subscribePresence(client, userId, (snapshot) => {
      setPresence(snapshot);
    });
    setPresence(getUserPresence(client, userId));
    void (async () => {
      try {
        const [profile, status, tz] = await Promise.all([
          getUserProfile(client, userId),
          getUserAccountDataLike<unknown>(client, userId, USER_STATUS_EVENT_TYPE),
          getUserAccountDataLike<unknown>(client, userId, USER_TIMEZONE_EVENT_TYPE),
        ]);
        if (cancelled) return;
        setDisplayName(displayNameForUser(profile.displayName ?? fallback?.displayName, userId));
        setAvatarUrl(profile.avatarUrl ?? fallback?.avatarUrl ?? null);
        setStatusData(normaliseStatus(status));
        setTimezone(validTimezone(tz));
      } catch {
        if (cancelled) return;
        setStatusData({ status: null, statusMsg: null, meetingUrl: null });
        setTimezone(null);
      }
    })();
    return () => {
      cancelled = true;
      unsub();
    };
  }, [client, fallback?.avatarUrl, fallback?.displayName, userId]);

  useEffect(() => {
    if (!timezone) return;
    const id = window.setInterval(() => setTick(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [timezone]);

  const localTime = useMemo(() => formatLocalTime(timezone, tick), [timezone, tick]);

  return {
    displayName,
    avatarUrl,
    matrixId: userId,
    presence,
    status: statusData.status,
    statusMsg: statusData.statusMsg,
    timezone,
    localTime,
    meetingUrl: statusData.meetingUrl,
  };
}
